// src/content/legal.ts
//
// 法務ページの本文は**ここ1か所**に置く。
//
// なぜ（2026-09-27）:
//   プライバシーポリシーはアプリ内（設定画面）にしか無く、`/privacy-policy` は
//   ルートが無いため **SPA の殻が返るだけ**だった（App Store のサポートページから
//   張られているリンクが実質切れていた）。静的HTMLを別に書くと本文が2つに割れて
//   片方だけ古くなるので、**本文はこのファイルを正とし、画面も静的HTMLもここから作る**。

export interface LegalSection {
  heading?: string;
  body: string[];
}

export interface LegalDoc {
  slug: string;
  title: string;
  description: string;
  updated: string;
  sections: LegalSection[];
}

// 公開面の事業者名は**屋号 TechAthletes に統一**する（司令塔 2026-09-28・CEO の秘匿方針）。
// 以前は別の屋号を書いていたが、**本名由来の手がかりになり得る**ため公開面から外した。
// T番号を出さないのと同じ理由（公開された文字列から本人に到達させない）。
const OPERATOR = 'TechAthletes';
// 公開面の問い合わせ先は **contact@tech-athletes.com** に統一する
// （司令塔のチェックリスト 2026-10-06）。以前は個人の Gmail と hello@ が混在しており、
// ①個人のアドレスが公開面に出る ②特商法表記とプライバシーポリシーで宛先が違う
// という2つの問題があった。宛先が複数あると、削除依頼が片方に出されて取りこぼす。
const CONTACT = 'contact@tech-athletes.com';

export const PRIVACY_POLICY: LegalDoc = {
  slug: 'privacy-policy',
  title: 'プライバシーポリシー',
  description: '診断士RoadMap のプライバシーポリシー。取得する情報、利用目的、第三者提供、データの削除方法について記載しています。',
  updated: '2026-09-27',
  sections: [
    {
      body: [
        `${OPERATOR}（以下「当社」といいます。）は、ユーザーが安心して当社サービス「診断士RoadMap」をご利用いただけるよう、個人情報の保護と適切な取り扱いを推進いたします。`,
      ],
    },
    {
      heading: '1. 収集する情報',
      body: [
        '・アカウント情報（メールアドレス、表示名、プロフィール画像）。ログイン方法として Google、Apple などの外部サービスを利用した場合、当該サービスから提供されるこれらの情報を取得します。',
        '・学習の記録（解答履歴、正誤、学習日、ブックマーク、掲示板への投稿）',
        '・決済に関する情報（購入履歴、サブスクリプションの状態）。**クレジットカード番号は当社では保持せず**、決済代行事業者（Stripe）が処理します。',
        '・サービス利用履歴、アクセスログ',
      ],
    },
    {
      heading: '2. 利用目的',
      body: [
        '・サービスの提供および改善',
        '・学習状況の記録と表示（復習計画、学習の可視化）',
        '・ユーザーサポート',
        '・決済処理および購入履歴の管理',
      ],
    },
    {
      heading: '3. 第三者提供',
      body: [
        '法令に基づく場合を除き、あらかじめユーザーの同意を得ることなく第三者に提供しません。',
        'サービスの運営のため、以下の事業者を利用しています。｜Google（Firebase：認証・データ保存・アクセス解析）｜Stripe（決済処理）',
      ],
    },
    {
      heading: '4. セキュリティ',
      body: ['情報の漏えい・紛失・改ざんを防止するため、適切な安全管理措置を講じます。'],
    },
    {
      heading: '5. データの削除',
      body: [
        'アプリ内の「設定」→「アカウントを削除」から、アカウントと学習データを削除できます。削除すると学習履歴は復元できません。',
        `アプリを開けない場合は ${CONTACT} までご連絡ください。ご本人確認のうえ削除いたします。`,
        '詳しい手順は データ削除の手続き のページをご覧ください。',
      ],
    },
    {
      heading: '6. アクセス解析ツールについて',
      body: [
        '当サイトでは、利用状況を把握するために Google アナリティクス（GA4）を利用しています。このツールはトラフィックデータの収集のために Cookie を使用します。収集されるデータは匿名で収集されており、個人を特定するものではありません。',
        'Google アナリティクスによるデータ収集を拒否する場合は、Google が提供するオプトアウト用のアドオンをご利用ください。｜https://tools.google.com/dlpage/gaoptout',
      ],
    },
    {
      heading: '7. 第三者配信の広告サービスについて',
      body: [
        '当サイトでは、第三者配信の広告サービス（Google AdSense を含みます）を利用する場合があります。',
        'このような広告配信事業者は、ユーザーの興味に応じた広告を表示するために Cookie を使用することがあります。Cookie を無効にする設定や、Google AdSense に関する詳細は、以下をご覧ください。',
        'パーソナライズ広告の設定 ｜ https://adssettings.google.com/',
        '広告における Cookie の利用について（Google のポリシー） ｜ https://policies.google.com/technologies/ads',
        '第三者配信事業者のオプトアウト ｜ https://optout.aboutads.info/',
        '広告の表示は、学習のための記事・解説ページに限ります。ログイン画面と採点画面には広告を表示しません。',
      ],
    },
    {
      heading: '8. お問い合わせ',
      body: [`${OPERATOR}　Email: ${CONTACT}`],
    },
  ],
};

export const DATA_DELETION: LegalDoc = {
  slug: 'data-deletion',
  title: 'データ削除の手続き',
  description: '診断士RoadMap のアカウントと学習データを削除する方法。アプリからの削除手順と、メールでの削除依頼の窓口を案内します。',
  updated: '2026-09-27',
  sections: [
    {
      body: ['診断士RoadMap に保存されたアカウントと学習データは、いつでも削除できます。'],
    },
    {
      heading: '方法1: アプリから削除する',
      body: [
        '1. 診断士RoadMap にログインします',
        '2. 「設定」を開きます',
        '3. 「アカウントを削除」を選びます',
        '4. 確認のうえ実行します',
        '削除すると、アカウント・学習履歴・掲示板への投稿を含むデータが削除され、**復元できません**。',
      ],
    },
    {
      heading: '方法2: メールで依頼する',
      body: [
        `アプリにログインできない場合は、${CONTACT} 宛に「データ削除希望」とご連絡ください。`,
        'ご登録のメールアドレスからお送りいただくか、ご本人確認にご協力ください。',
        '受付から30日以内に削除し、完了をご連絡します。',
      ],
    },
    {
      heading: '削除されるもの',
      body: [
        '・アカウント情報（メールアドレス、表示名、プロフィール画像）',
        '・学習の記録（解答履歴、学習日、ブックマーク）',
        '・掲示板およびコミュニティへの投稿',
        'なお、法令により保存が義務づけられている取引記録（決済の記録など）は、所定の期間を経過するまで保管する場合があります。',
      ],
    },
    {
      heading: 'お問い合わせ',
      body: [`${OPERATOR}　Email: ${CONTACT}`],
    },
  ],
};

/**
 * 特定商取引法に基づく表記。
 *
 * **本名・住所・電話番号は書かない**（特商法11条ただし書により「請求があれば遅滞なく開示」で省略）。
 * **インボイスの登録番号（T番号）も書かない** — 国税庁の公表サイトで T番号から
 * 個人事業主の本名が引けるため（司令塔 2026-09-27）。表記・購入画面・領収書のいずれにも出さない。
 *
 * 記載内容はすべて 2026-09-28 に実装・Stripe の設定を実測して確認したもの:
 *   価格   price_1SQrQOJm6nYxRNS1wU8ldhBN = 500 JPY / month（商品名「診断士RoadMap Premium」）
 *   支払方法 Checkout の payment_method_types = ['card']
 *   解約   Customer Portal(bpc_1UFTjfJm6nYxRNS1eMaegiLJ) の subscription_cancel = 有効・at_period_end
 *
 * 2026-10-12 追記（2次直前パス・司令塔承認 A′）:
 *   価格   price_1UPfOkJm6nYxRNS1hoTYZyWg = 980 JPY / month
 *          （商品名「診断士RoadMap 2次直前パス（2026年10月26日まで）」）
 *   期限   購読が出来た直後にサーバが cancel_at = 2026-10-26 23:59:59 JST を立てる。
 *          期日に Stripe が終了させるので**請求は1回だけ**。自動更新はしない。
 *          値は src/config/pass2ji.ts と functions/handlers/stripeHandler.ts にも同じものがある。
 *   販売   2026-10-25 いっぱいで止める（サーバが日付で断る）。
 */
export const COMMERCE_DISCLOSURE: LegalDoc = {
  slug: 'tokushoho',
  title: '特定商取引法に基づく表記',
  description: '診断士RoadMap（TechAthletes）の特定商取引法に基づく表記。販売価格、支払方法、提供時期、解約の方法を記載しています。',
  updated: '2026-10-12',
  sections: [
    { heading: '販売事業者', body: ['TechAthletes（屋号）'] },
    {
      heading: '運営責任者・所在地・電話番号',
      body: ['請求があった場合には遅滞なく開示いたします。'],
    },
    { heading: 'お問い合わせ', body: [CONTACT, 'お問い合わせはメールにて承ります。'] },
    {
      heading: '販売価格',
      body: [
        '診断士RoadMap Premium：**月額 500円**',
        '2次直前パス：**980円**（お支払いは1回のみ・**2026年10月26日まで**ご利用いただけます）',
        '2次直前パスは期間限定の販売で、**2026年10月25日**をもって販売を終了します。自動更新はありません。',
        '表示している金額のみをお支払いいただきます。別途、消費税が加算されることはありません。',
        '無料でご利用いただける範囲があり、Premium は任意です。',
      ],
    },
    {
      heading: '商品代金以外に必要な費用',
      body: ['インターネット接続に必要な通信料金は、お客様のご負担となります。'],
    },
    {
      heading: '支払方法',
      body: [
        'クレジットカード・デビットカード（決済代行：Stripe）',
        '**カード情報は当社では保持せず**、Stripe が処理します。',
      ],
    },
    {
      heading: 'お支払い時期',
      body: [
        '診断士RoadMap Premium：お申し込み時に初回分を決済し、以後は**1か月ごとに自動で更新**されます。更新日はお申し込み日を基準とします。',
        '2次直前パス：お申し込み時に 980円を**1回だけ**決済します。**2026年10月26日に自動で終了し、それ以降の請求はありません。**',
      ],
    },
    {
      heading: 'サービスの提供時期',
      body: ['決済の完了後、ただちにご利用いただけます。'],
    },
    {
      heading: '解約（定期購読の停止）',
      body: [
        'Web 版（chusho-app.web.app）のアカウントページ →「**購読を管理**」から、いつでも解約できます。',
        '解約は**そのお支払い期間の終了時**に適用され、期間の終わりまでは引き続きご利用いただけます。',
        '**日割りでの返金は行っておりません。**',
        '2次直前パスは**期日に自動で終了する**ため、解約の手続きは必要ありません。期日より前に停止したい場合は、同じ「購読を管理」から解約できます。',
        'iOS アプリからご購入の場合は、Apple の「サブスクリプション」設定から解約してください。',
      ],
    },
    {
      heading: '返品・キャンセルについて',
      body: [
        'サービスの性質上、提供開始後の返品はお受けしておりません。',
        // バッククォートにする（シングルクォートだったため画面に `${CONTACT}` の文字がそのまま出ていた）
        `当社の不具合により利用できなかった場合など、個別のご事情は ${CONTACT} までご相談ください。`,
      ],
    },
    {
      heading: '動作環境',
      body: [
        'Web：最新版の Google Chrome / Safari / Microsoft Edge / Firefox',
        'iOS アプリ：iOS 15 以降',
        'インターネット接続が必要です。',
      ],
    },
  ],
};

export const OPERATOR_INFO: LegalDoc = {
  slug: 'operator',
  title: '運営者情報',
  description: '診断士RoadMap の運営者情報。屋号・事業内容・お問い合わせ先を記載しています。',
  updated: '2026-10-06',
  sections: [
    {
      body: [
        `当サイトおよび「診断士RoadMap」は ${OPERATOR} が個人事業として運営しています。`,
      ],
    },
    { heading: '屋号', body: [OPERATOR] },
    {
      heading: '事業内容',
      body: [
        '中小企業診断士試験の学習支援サービス（Web / iOS / Android）の開発・運営',
        '試験範囲の論点解説、過去問演習、AIによる記述答案の採点、学習記録の管理',
      ],
    },
    {
      heading: 'お問い合わせ',
      body: [
        `Email: ${CONTACT}`,
        'お問い合わせはメールにて承ります。内容により回答に数日いただく場合があります。',
      ],
    },
    {
      heading: '運営責任者・所在地・電話番号',
      body: [
        '特定商取引法に基づく表記のとおり、ご請求があれば遅滞なく開示いたします。上記のメールアドレスまでご連絡ください。',
      ],
    },
    {
      heading: '関連ページ',
      body: [
        'プライバシーポリシー ｜ /privacy-policy',
        '広告・アフィリエイトの方針 ｜ /ads-policy',
        '特定商取引法に基づく表記 ｜ /tokushoho',
        'データ削除の手続き ｜ /data-deletion',
      ],
    },
  ],
};

export const ADS_POLICY: LegalDoc = {
  slug: 'ads-policy',
  title: '広告・アフィリエイトの方針',
  description: '診断士RoadMap における広告およびアフィリエイトの方針。広告を表示する場所と表示しない場所、表記の方法について記載しています。',
  updated: '2026-10-06',
  sections: [
    {
      body: [
        `${OPERATOR} は、サービスを無料で提供し続けるための費用にあてるため、サイトの一部に広告を掲載する場合があります。掲載にあたっては次の方針を守ります。`,
      ],
    },
    {
      heading: '広告を表示する場所',
      body: [
        '論点解説・勉強法・年度別傾向などの**記事ページ**',
        'ミニ模試・計算ドリルなど、学習用の**公開ページ**',
      ],
    },
    {
      heading: '広告を表示しない場所',
      body: [
        '**ログイン・新規登録の画面**',
        '**答案の採点画面と採点結果の画面**（答案に集中してもらう場所なので置きません）',
        '**お支払いに関する画面**',
      ],
    },
    {
      heading: '第三者配信の広告について',
      body: [
        '第三者配信の広告サービス（Google AdSense を含みます）を利用する場合があります。広告配信事業者が Cookie を使用することがあり、無効にする方法はプライバシーポリシーに記載しています。',
        'プライバシーポリシー ｜ /privacy-policy',
      ],
    },
    {
      heading: 'アフィリエイトについて',
      body: [
        '書籍や教材を紹介する際に、アフィリエイトプログラムを利用する場合があります。その場合は、**該当する記事の中にその旨を明記**します。',
        '紹介するかどうかは、受験生にとって役に立つかどうかで判断します。紹介料の多さで順番を変えることはしません。',
        '広告・アフィリエイトのリンクと、サイト内の学習コンテンツへのリンクは、見分けがつくように分けて表示します。',
      ],
    },
    {
      heading: '書かないこと',
      body: [
        '合格率・得点・利用者数など、**実際のデータが無い数字は書きません**。',
        '体験談を装った文章は書きません。',
        '過去問や他サイトの文章の転載はしません。サイト内の問題は自作です。',
      ],
    },
    {
      heading: 'お問い合わせ',
      body: [`${OPERATOR}　Email: ${CONTACT}`],
    },
  ],
};

export const LEGAL_DOCS: LegalDoc[] = [
  PRIVACY_POLICY,
  DATA_DELETION,
  COMMERCE_DISCLOSURE,
  OPERATOR_INFO,
  ADS_POLICY,
];
